import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@hey404/ui";

// How many rows a long list or table shows before it scrolls inside itself.
export const TABLE_ROWS = 7;

/**
 * The card around a `.data-table`. Up to `rows` rows it is a plain card; past
 * that it caps its height to about that many rows and scrolls inside itself
 * (the header stays pinned), fades its bottom edge while there is more below,
 * and says how many there are under it. The page never stretches for a long
 * history.
 */
export function TableScroll({
  count,
  noun,
  rows = TABLE_ROWS,
  className,
  children,
}: {
  /** Total rows in the table. */
  count: number;
  /** Plural noun for the footer: "12 órdenes · desliza para ver más". */
  noun: string;
  rows?: number;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [atEnd, setAtEnd] = useState(false);
  const scrolls = count > rows;
  const checkEnd = () => {
    const box = ref.current;
    if (box) setAtEnd(box.scrollTop + box.clientHeight >= box.scrollHeight - 4);
  };
  useEffect(checkEnd, [count]);
  return (
    <div className={className}>
      <div className="relative">
        <div
          ref={ref}
          onScroll={scrolls ? checkEnd : undefined}
          tabIndex={scrolls ? 0 : undefined}
          aria-label={scrolls ? `Tabla de ${noun}` : undefined}
          // A row is ~3.45rem and the header ~2.9rem: show exactly `rows`.
          style={scrolls ? { maxHeight: `${rows * 3.45 + 2.9}rem` } : undefined}
          className={cn(
            "data-table-card",
            scrolls &&
              "overflow-y-auto overscroll-contain focus-visible:outline-none",
          )}
        >
          {children}
        </div>
        {/* A fade over the last row, inside the card's border, while there's
            more below; a mask would also erase the border and shadow. */}
        {scrolls && !atEnd && (
          <span
            aria-hidden="true"
            className="from-bone pointer-events-none absolute inset-x-[var(--border-surface)] bottom-[var(--border-surface)] h-12 bg-gradient-to-t to-transparent"
          />
        )}
      </div>
      {scrolls && (
        <p className="text-ink-soft mt-2 font-mono text-xs">
          {count} {noun} · desliza dentro de la tabla para ver más
        </p>
      )}
    </div>
  );
}
